/* eslint-disable @typescript-eslint/no-explicit-any */

import './brokers-list.scss';

import {
  CheckedState,
  Option,
} from '../../../../presentation/components/table-filter';
import {
  PersonPaginationSort,
  PersonSortKey,
  useGetBrokers,
} from '@converge/graphql';
import React, { useEffect, useState } from 'react';

import BrokerFilter from './brokers-filter';
import { ConvergeNavigationUrls } from '../../../../contexts';
import { SortOrder } from '@converge/data';
import {
  CustomizedTable,
  createTextColumn,
  type TableColumn,
} from '../../../../presentation/components/new-customized-table';
import { capitalizeFirstLetterRemoveUnderLine } from '@converge/utils';
import { Box } from '@mui/material';
import { ConvergeTooltip } from '../../../../presentation/components/converge-tooltip';

interface BrokerRow {
  id: string;
  broker: string;
  brokerage: string;
  uwRegion: string;
  city: string;
  team: string;
  underwriterName: string;
  bdmName: string;
  onboarded: string;
  status: string;
  brokerageId: string;
}

const cleanFilters = (filters: CheckedState): { [key: string]: any } => {
  const result: { [key: string]: any } = {};

  Object.keys(filters).forEach((key) => {
    const value = filters[key];
    if (Array.isArray(value) && value.length > 0) {
      switch (key) {
        case 'broker':
          result['uuids'] = value;
          break;
        case 'brokerage':
          result['brokerages'] = value;
          break;
        case 'bdm':
          result['bdmUserUuids'] = value;
          break;
        case 'uw':
          result['underwriterUserUuids'] = value;
          break;
        case 'uwRegion':
          result['underwritingTerritories'] = value;
          break;
        case 'status':
          result['statuses'] = value;
          break;
        case 'onboardedDate': {
          try {
            const parsed = JSON.parse(value[0]);
            result['createdAt'] = {
              start: parsed[0] ?? null,
              end: parsed[1] ?? null,
            };
          } catch {
            /* ignore malformed */
          }
          break;
        }
        default:
          result[key] = value;
      }
    }
  });

  return result;
};

const defaultFilters = {
  broker: [],
  brokerage: [],
  bdm: [],
  uw: [],
  uwRegion: [],
  onboardedDate: [],
  status: [],
};

const reverseSortFieldMap = {
  [PersonSortKey.FirstName]: 'broker',
  [PersonSortKey.BrokeragePrimaryLegalName]: 'brokerage',
  [PersonSortKey.TeamName]: 'team',
  [PersonSortKey.Status]: 'status',
  [PersonSortKey.UnderwritingTerritoryName]: 'uwRegion',
  [PersonSortKey.BrokeragePrimaryCity]: 'city',
  [PersonSortKey.BdmUserFirstName]: 'bdmName',
  [PersonSortKey.UnderwriterUserFirstName]: 'underwriterName',
  [PersonSortKey.CreatedAt]: 'onboarded',
};

const defaultSorting: PersonPaginationSort = {
  key: PersonSortKey.CreatedAt,
  order: SortOrder.Desc,
};

const BrokersList = ({
  defaultBrokerages,
}: {
  defaultBrokerages: Option[];
}) => {
  const localStoragePageSize: number = parseInt(
    localStorage.getItem('brokerPageSize') ?? '0'
  );
  const localStoragePageNumber: number = parseInt(
    localStorage.getItem('brokerPageNumber') ?? '0'
  );

  const localStorageSorting: PersonPaginationSort = JSON.parse(
    localStorage.getItem('brokerSorting') ?? '{}'
  );

  const isLocalStorageSorting = Object.keys(localStorageSorting).length > 0;

  const [currentPage, setCurrentPage] = useState<number>(
    localStoragePageNumber || 0
  );
  const [pageSize, setPageSize] = useState<number>(localStoragePageSize || 10);

  const [brokersFilters, setBrokersFilters] = useState<CheckedState>(
    JSON.parse(localStorage.getItem('brokerFilters') || '{}') || {}
  );
  const [currentFilters, setCurrentFilters] = useState<CheckedState>(
    JSON.parse(localStorage.getItem('brokerFilters') || '{}') || {}
  );

  const [filterLabels, setFilterLabels] = useState<CheckedState>(
    JSON.parse(localStorage.getItem('brokerFilterLabels') || 'null') ||
      defaultFilters
  );

  const [currentFilterLabels, setCurrentFilterLabels] = useState<CheckedState>(
    JSON.parse(localStorage.getItem('brokerFilterLabels') || 'null') ||
      defaultFilters
  );

  const brokerFiltersStorage = JSON.parse(
    localStorage.getItem('brokerFilters') || '{}'
  );

  const [currentSort, setCurrentSort] = useState<PersonPaginationSort>(
    isLocalStorageSorting ? localStorageSorting : defaultSorting
  );

  const { brokers, loading, data } = useGetBrokers({
    pageNumber: currentPage,
    pageSize: pageSize,
    sort: currentSort && currentSort.key ? currentSort : undefined,
    filters: cleanFilters(brokersFilters),
  });

  useEffect(() => {
    if (brokerFiltersStorage && Object.keys(brokerFiltersStorage)?.length) {
      setCurrentFilters(brokerFiltersStorage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBrokerClick = (value: any, row: BrokerRow) => {
    window.location.href = ConvergeNavigationUrls.broker({
      brokerUuid: row.id,
    });
  };
  const handleBrokerageClick = (value: any, row: BrokerRow) => {
    window.location.href = ConvergeNavigationUrls.brokerage({
      brokerageUuid: row.brokerageId,
    });
  };

  const columns: TableColumn<BrokerRow>[] = [
    {
      id: 'broker',
      label: 'BROKER',
      flex: '3.5',
      sortable: true,
      headerTestId: 'broker-header',
      render: (value, row, rowIndex) => (
        <ConvergeTooltip title={String(value || '—')}>
          <div
            data-testid={`broker_${rowIndex}`}
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              width: '100%',
              cursor: 'pointer',
              color: 'black',
              textDecoration: 'underline',
            }}
            onClick={() => handleBrokerClick(value, row)}
          >
            {String(value || '—')}
          </div>
        </ConvergeTooltip>
      ),
    },
    createTextColumn<BrokerRow>('brokerage', 'BROKERAGE', {
      flex: '2.5',
      sortable: true,
      headerTestId: 'brokerage-header',
      render: (value, row, rowIndex) => {
        const isEmpty = value === '—';
        return (
          <ConvergeTooltip title={String(value || '—')}>
            <div
              data-testid={`brokerage_${rowIndex}`}
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                width: '100%',
                cursor: isEmpty ? 'default' : 'pointer',
                color: 'black',
                textDecoration: isEmpty ? 'none' : 'underline',
              }}
              onClick={
                isEmpty ? undefined : () => handleBrokerageClick(value, row)
              }
            >
              {String(value || '—')}
            </div>
          </ConvergeTooltip>
        );
      },
    }),
    createTextColumn<BrokerRow>('bdmName', 'BDM', {
      flex: '1.2',
      sortable: true,
      headerTestId: 'bdm-header',
      render: (value, _row, rowIndex) => (
        <span data-testid={`bdm_${rowIndex}`}>{String(value || '—')}</span>
      ),
    }),
    createTextColumn<BrokerRow>('underwriterName', 'UW', {
      flex: '1.5',
      sortable: true,
      headerTestId: 'uw-header',
      render: (value, _row, rowIndex) => (
        <ConvergeTooltip title={String(value || '—')}>
          <div
            data-testid={`uw_${rowIndex}`}
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              width: '100%',
            }}
          >
            {String(value || '—')}
          </div>
        </ConvergeTooltip>
      ),
    }),
    createTextColumn<BrokerRow>('uwRegion', 'UW REGION', {
      flex: '1.5',
      sortable: true,
      headerTestId: 'uw-region-header',
      render: (value, _row, rowIndex) => (
        <span data-testid={`uw-region_${rowIndex}`}>
          {String(value || '—')}
        </span>
      ),
    }),
    createTextColumn<BrokerRow>('city', 'OFFICE/CITY', {
      flex: '1.5',
      sortable: false,
      headerTestId: 'city-header',
      render: (value, _row, rowIndex) => (
        <span data-testid={`city_${rowIndex}`}>{String(value || '—')}</span>
      ),
    }),
    createTextColumn<BrokerRow>('team', 'TEAM', {
      flex: '1.0',
      sortable: true,
      headerTestId: 'team-header',
      render: (value, _row, rowIndex) => (
        <ConvergeTooltip title={String(value || '—')}>
          <div
            data-testid={`team_${rowIndex}`}
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              width: '100%',
            }}
          >
            {String(value || '—')}
          </div>
        </ConvergeTooltip>
      ),
    }),
    createTextColumn<BrokerRow>('onboarded', 'ONBOARDED', {
      flex: '1.5',
      sortable: true,
      headerTestId: 'onboarded-header',
      render: (value, _row, rowIndex) => (
        <span data-testid={`onboarded_${rowIndex}`}>
          {String(value || '—')}
        </span>
      ),
    }),
    {
      id: 'status',
      label: 'STATUS',
      flex: '1.',
      sortable: true,
      headerTestId: 'status-header',
      render: (value, _row, rowIndex) => {
        const displayValue =
          value && capitalizeFirstLetterRemoveUnderLine(value as string);
        const isActive = displayValue === 'Active';
        const color = isActive ? '#2E7D32' : '#D32F2F';
        const backgroundColor = isActive ? '#EDF7ED' : '#FDEDED';

        return (
          <div>
            <span
              data-testid={`status_${rowIndex}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '4px 12px',
                borderRadius: '16px',
                border: `1px solid ${color}`,
                backgroundColor: `${backgroundColor}`,
                fontSize: '12px',
                fontWeight: '500',
                color: color,
                minWidth: '80px',
                justifyContent: 'center',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {displayValue || '—'}
            </span>
          </div>
        );
      },
    },
  ];

  const handleSortModeChange = (
    columnId: string,
    sortOrder: 'asc' | 'desc'
  ) => {
    const sort = {
      key: PersonSortKey.CreatedAt,
      order: sortOrder === 'asc' ? SortOrder.Asc : SortOrder.Desc,
    };

    switch (columnId) {
      case 'broker':
        sort.key = PersonSortKey.FirstName;
        break;
      case 'brokerage':
        sort.key = PersonSortKey.BrokeragePrimaryLegalName;
        break;
      case 'team':
        sort.key = PersonSortKey.TeamName;
        break;
      case 'status':
        sort.key = PersonSortKey.Status;
        break;
      case 'uwRegion':
        sort.key = PersonSortKey.UnderwritingTerritoryName;
        break;
      case 'city':
        sort.key = PersonSortKey.BrokeragePrimaryCity;
        break;
      case 'bdmName':
        sort.key = PersonSortKey.BdmUserFirstName;
        break;
      case 'underwriterName':
        sort.key = PersonSortKey.UnderwriterUserFirstName;
        break;
      case 'licenses':
        sort.key = PersonSortKey.LicenseCount;
        break;
      case 'onboarded':
        sort.key = PersonSortKey.CreatedAt;
        break;
    }

    localStorage.setItem('brokerSorting', JSON.stringify(sort));
    setCurrentSort(sort);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    localStorage.setItem('brokerPageNumber', newPage.toString());
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
    localStorage.setItem('brokerPageSize', newPageSize.toString());
    localStorage.setItem('brokerPageNumber', '0');
  };

  const handleResetPageNumber = () => {
    setCurrentPage(0);
    localStorage.setItem('brokerPageNumber', '0');
  };

  return (
    <Box className="broker-list">
      <BrokerFilter
        setBrokerFilters={setBrokersFilters}
        currentFilters={currentFilters}
        setCurrentFilters={setCurrentFilters}
        currentFilterLabels={currentFilterLabels}
        filterLabels={filterLabels}
        setFilterLabels={setFilterLabels}
        resetPageNumber={handleResetPageNumber}
        setCurrentFilterLabels={setCurrentFilterLabels}
      />

      <CustomizedTable<BrokerRow>
        title=""
        columns={columns}
        data={brokers as BrokerRow[]}
        enableSorting={true}
        enablePagination={true}
        loading={loading}
        rowTestIdPrefix="broker"
        maxHeight="calc(80vh - 225px)"
        // Server-side sorting
        onSortChange={(columnId, sortOrder) =>
          handleSortModeChange(columnId as string, sortOrder)
        }
        onSortReset={() => {
          localStorage.setItem('brokerSorting', JSON.stringify(defaultSorting));
          setCurrentSort(defaultSorting);
        }}
        currentSortColumn={
          reverseSortFieldMap[
            currentSort.key as keyof typeof reverseSortFieldMap
          ] as keyof BrokerRow
        }
        currentSortOrder={currentSort.order === SortOrder.Asc ? 'asc' : 'desc'}
        // Server-side pagination
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        currentPage={currentPage}
        totalItems={data?.persons?.pageInfo?.totalResults}
        defaultRowsPerPage={pageSize}
      />
    </Box>
  );
};

export default BrokersList;
